// ui/presenters/chargeLineCaption.js
import { i18n } from 'boot/i18n';
import { formatCalendarDate } from 'src/utils/DateUtils';

import { CODE_LIFECYCLE } from './chargeLineRowPresenter';

const KEY = 'settings.billing.chargeMaster.codeState';

/**
 * Which date each state is talking about. `expiring` and `expired` are the same fact read from
 * either side of today, so both name `retiredOn`; a code that has not started names `validFrom`.
 */
const DATE_FIELD = {
  [CODE_LIFECYCLE.EXPIRING]: 'retiredOn',
  [CODE_LIFECYCLE.EXPIRED]: 'retiredOn',
  [CODE_LIFECYCLE.FUTURE]: 'validFrom',
};

/**
 * What a row says about the life of its code, or null when there is nothing worth saying.
 *
 * SILENT ON `active`, AND THAT IS THE POINT. Most rows are ordinary, and a marker on every one of
 * them would say nothing while making the exceptions harder to find. `unknown` is silent here too —
 * the row already reads "Unknown code" where the code would be, and a second sentence about the
 * dates of a code the catalog does not hold would be inventing detail.
 *
 * ANCHORED TO A DATE RATHER THAN A WORD. "Expired" alone leaves the reader to guess whether a
 * charge still applies to the service they are looking at; the boundary is the thing they need, and
 * it is the same boundary the resolver uses.
 */
export function codeStateLabel(row) {
  const field = DATE_FIELD[row?.lifecycle];

  if (field === undefined || row[field] === null) {
    return null;
  }

  return i18n.t(`${KEY}.${row.lifecycle}`, { date: formatCalendarDate(row[field]) });
} // codeStateLabel()

/**
 * The row's secondary line: what the code covers, led by its state when it has one.
 *
 * ONE STRING FOR BOTH SURFACES, so the read page and the editor cannot drift into describing the
 * same row differently. The state leads because it is the exception and the descriptor is always
 * there, and the two are joined through a key rather than a hard-coded separator so a locale can
 * order and punctuate them for itself.
 */
export function chargeLineCaption(row) {
  const state = codeStateLabel(row);
  const descriptor = row?.descriptor ?? '';

  if (state === null) {
    return descriptor;
  }

  return descriptor === ''
    ? state
    : i18n.t(`${KEY}.captionWithState`, { state, descriptor });
} // chargeLineCaption()
